import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { MapPin, Building, FileText, Check } from 'lucide-react'

const LOCATIONS = [
  'Hawaii', 'California', 'Florida', 'New York', 'Texas',
  'Australia', 'Portugal', 'Spain', 'Indonesia', 'Mexico',
  'Costa Rica', 'Brazil', 'South Africa', 'France', 'Other'
]

export default function SchoolSetup({ onComplete }) {
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    location: '',
    custom_location: '',
    description: '',
    beach_names: '',
  })

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async () => {
    setSaving(true)
    const location = form.location === 'Other' ? form.custom_location : form.location

    const { data: school, error } = await supabase
      .from('schools')
      .insert({
        owner_id: user.id,
        name: form.name,
        location,
        description: form.description,
      })
      .select()
      .single()

    if (!error && school) {
      // Link admin profile to this school
      await supabase
        .from('profiles')
        .update({ school_id: school.id })
        .eq('id', user.id)
    }

    setSaving(false)
    onComplete(school)
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{ width: '100%', maxWidth: 500 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: 52, height: 52, background: 'var(--teal)',
            borderRadius: '50%', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 26, marginBottom: '1rem'
          }}>🏄</div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: 4 }}>
            Set up your surf school
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem' }}>
            This takes 2 minutes. You can change everything later.
          </p>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20, padding: '2rem', backdropFilter: 'blur(12px)'
        }}>
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'white', marginBottom: 4 }}>
                What's your school called?
              </h2>

              <div className="form-group">
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <Building size={12} style={{ display: 'inline', marginRight: 5 }} />
                  School Name
                </label>
                <input
                  className="form-control"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }}
                  placeholder="e.g. Pipeline Surf School"
                  value={form.name}
                  onChange={set('name')}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <MapPin size={12} style={{ display: 'inline', marginRight: 5 }} />
                  Location
                </label>
                <select
                  className="form-control"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: form.location ? 'white' : 'rgba(255,255,255,0.35)' }}
                  value={form.location}
                  onChange={set('location')}
                >
                  <option value="" disabled>Select your location...</option>
                  {LOCATIONS.map(l => <option key={l} value={l} style={{ color: 'black' }}>{l}</option>)}
                </select>
              </div>

              {form.location === 'Other' && (
                <div className="form-group">
                  <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>Custom Location</label>
                  <input
                    className="form-control"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }}
                    placeholder="e.g. Bali, Indonesia"
                    value={form.custom_location}
                    onChange={set('custom_location')}
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <FileText size={12} style={{ display: 'inline', marginRight: 5 }} />
                  Short Description (optional)
                </label>
                <textarea
                  className="form-control"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', resize: 'none' }}
                  rows={3}
                  placeholder="Tell customers a bit about your school..."
                  value={form.description}
                  onChange={set('description')}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: 52, marginBottom: '1rem' }}>🎉</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', color: 'white', marginBottom: '0.75rem' }}>
                {form.name} is ready!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                Your school is set up. Now you can invite instructors, create lessons, and start taking bookings.
              </p>
              <div style={{ background: 'rgba(14,170,164,0.08)', border: '1px solid rgba(14,170,164,0.2)', borderRadius: 10, padding: '1rem', textAlign: 'left', fontSize: '0.825rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8 }}>
                <div>✅ School created: <strong style={{ color: 'white' }}>{form.name}</strong></div>
                <div>✅ Location: <strong style={{ color: 'white' }}>{form.location === 'Other' ? form.custom_location : form.location}</strong></div>
                <div>⏭ Next: Approve instructors as they join</div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem', gap: '0.75rem' }}>
            {step === 1 && (
              <button
                className="btn btn-primary"
                disabled={!form.name || !form.location || (form.location === 'Other' && !form.custom_location)}
                onClick={() => { handleSubmit(); setStep(2) }}
              >
                {saving ? 'Creating...' : 'Create My School →'}
              </button>
            )}
            {step === 2 && (
              <button className="btn btn-primary" onClick={() => onComplete()}>
                Go to Dashboard →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
